const SCROLL_TARGET_SELECTORS = [".main-panel", ".main-panel-dark"];
const LEGACY_PROGRESS_PILL_ID = "admin-video-scroll-capture-progress";
const FLASH_OVERLAY_ID = "admin-video-scroll-capture-flash";
const HIDE_CURSOR_STYLE_ID = "admin-video-scroll-capture-hide-cursor-style";
const HIDE_CURSOR_CLASS = "admin-video-scroll-capture-hide-cursor";
const CAPTURE_MODAL_SELECTOR = ".admin-video-scroll-capture-modal";
const ACTIVE_MODAL_PRIORITY_SCROLL_SELECTORS = [
  ".teacher-hub-class-style-preset-gallery-shell",
  ".teacher-hub-class-style-patterns-modal-body",
];
const EXCLUDED_SCROLL_TARGET_SELECTOR = [
  ".modal-backdrop",
  CAPTURE_MODAL_SELECTOR,
  `#${LEGACY_PROGRESS_PILL_ID}`,
  `#${FLASH_OVERLAY_ID}`,
].join(", ");
const DEFAULT_FADE_MS = 150;

export const ADMIN_VIDEO_SCROLL_DEFAULTS = {
  pixelsPerSecond: 120,
  flashStyle: "black-hold",
  flashDurationMs: 400,
  scrollToTopFirst: true,
  pauseAtBottomMs: 0,
  hideMouseWhileScrolling: true,
};

const clampNumber = (value, min, max, fallback) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return fallback;
  return Math.min(max, Math.max(min, numberValue));
};

const getDocument = (documentOverride) => (
  documentOverride || (typeof document !== "undefined" ? document : null)
);

const getWindow = (windowOverride) => (
  windowOverride || (typeof window !== "undefined" ? window : null)
);

const getDocumentScrollTarget = (doc) => (
  doc?.scrollingElement || doc?.documentElement || doc?.body || null
);

const hasScrollableOverflow = (element) => {
  const ownerDocument = element?.ownerDocument;
  if (!element || element === getDocumentScrollTarget(ownerDocument)) {
    return true;
  }

  const win = getWindow(ownerDocument?.defaultView);
  if (typeof win?.getComputedStyle !== "function") {
    return true;
  }

  const style = win.getComputedStyle(element);
  const overflowY = style.overflowY || style.overflow;
  return /(auto|scroll|overlay)/.test(overflowY);
};

const isScrollableElement = (element) => (
  Boolean(element && element.scrollHeight - element.clientHeight > 1 && hasScrollableOverflow(element))
);

const isExcludedScrollTarget = (element) => (
  Boolean(element?.closest?.(EXCLUDED_SCROLL_TARGET_SELECTOR))
);

const isCaptureSettingsModal = (modal) => (
  Boolean(modal?.matches?.(CAPTURE_MODAL_SELECTOR) || modal?.querySelector?.(CAPTURE_MODAL_SELECTOR))
);

const getElementDepth = (element, root) => {
  let depth = 0;
  let current = element;

  while (current && current !== root) {
    depth += 1;
    current = current.parentElement;
  }

  return depth;
};

const getScrollableDescendants = (root, { preferDeepest = false } = {}) => (
  [root, ...Array.from(root?.querySelectorAll?.("*") || [])]
    .filter((element) => isScrollableElement(element) && !isExcludedScrollTarget(element))
    .sort((first, second) => {
      if (preferDeepest) {
        const depthDelta = getElementDepth(second, root) - getElementDepth(first, root);
        if (depthDelta !== 0) return depthDelta;
      }

      return (second.scrollHeight - second.clientHeight) - (first.scrollHeight - first.clientHeight);
    })
);

const findActiveModalScrollableElement = (doc) => {
  if (!doc?.body) return null;

  const openModals = Array.from(doc.querySelectorAll(".modal.show, .modal[aria-modal='true'], .modal[role='dialog']"))
    .filter((modal) => !isCaptureSettingsModal(modal) && !isExcludedScrollTarget(modal))
    .reverse();

  for (const modal of openModals) {
    const priorityTarget = ACTIVE_MODAL_PRIORITY_SCROLL_SELECTORS
      .map((selector) => modal.querySelector(selector))
      .find((element) => isScrollableElement(element) && !isExcludedScrollTarget(element));
    if (priorityTarget) {
      return priorityTarget;
    }

    const [scrollTarget] = getScrollableDescendants(modal, { preferDeepest: true });
    if (scrollTarget) {
      return scrollTarget;
    }
  }

  return null;
};

const findLargestScrollableElement = (doc) => {
  if (!doc?.body) return null;

  return getScrollableDescendants(doc.body)[0] || null;
};

export function resolveAdminVideoScrollTarget(documentOverride) {
  const doc = getDocument(documentOverride);
  if (!doc) return null;

  const activeModalTarget = findActiveModalScrollableElement(doc);
  if (activeModalTarget) {
    return activeModalTarget;
  }

  const panelTarget = SCROLL_TARGET_SELECTORS
    .map((selector) => doc.querySelector(selector))
    .find(isScrollableElement);

  if (panelTarget) {
    return panelTarget;
  }

  const documentTarget = getDocumentScrollTarget(doc);
  if (isScrollableElement(documentTarget)) {
    return documentTarget;
  }

  return findLargestScrollableElement(doc) || documentTarget;
}

export function getAdminVideoScrollDistance(target) {
  if (!target) return 0;
  return Math.max(0, target.scrollHeight - target.clientHeight - target.scrollTop);
}

export function calculateAdminVideoScrollDurationMs(distance, pixelsPerSecond) {
  const speed = clampNumber(pixelsPerSecond, 10, 2000, ADMIN_VIDEO_SCROLL_DEFAULTS.pixelsPerSecond);
  return Math.max(0, Math.round((Math.max(0, distance) / speed) * 1000));
}

export function getAdminVideoScrollPreview(options = {}) {
  const doc = getDocument(options.document);
  const target = resolveAdminVideoScrollTarget(doc);
  const startTop = options.scrollToTopFirst === false ? target?.scrollTop || 0 : 0;
  const distance = target
    ? Math.max(0, target.scrollHeight - target.clientHeight - startTop)
    : 0;

  return {
    canScroll: distance > 1,
    distance,
    durationMs: calculateAdminVideoScrollDurationMs(distance, options.pixelsPerSecond),
    target,
  };
}

const removeElementById = (doc, id) => {
  const existing = doc?.getElementById?.(id);
  if (existing?.parentNode) {
    existing.parentNode.removeChild(existing);
  }
};

const applyMouseCursorHiding = (doc) => {
  if (!doc?.documentElement || !doc?.head) {
    return () => {};
  }

  removeElementById(doc, HIDE_CURSOR_STYLE_ID);
  const styleElement = doc.createElement("style");
  styleElement.id = HIDE_CURSOR_STYLE_ID;
  styleElement.textContent = `
    html.${HIDE_CURSOR_CLASS},
    html.${HIDE_CURSOR_CLASS} * {
      cursor: none !important;
    }
  `;
  doc.head.appendChild(styleElement);
  doc.documentElement.classList.add(HIDE_CURSOR_CLASS);

  return () => {
    doc.documentElement.classList.remove(HIDE_CURSOR_CLASS);
    removeElementById(doc, HIDE_CURSOR_STYLE_ID);
  };
};

const waitForTimeout = (win, delayMs, shouldCancel) => (
  new Promise((resolve) => {
    if (delayMs <= 0 || shouldCancel()) {
      resolve();
      return;
    }

    const timeoutId = win.setTimeout(resolve, delayMs);
    if (shouldCancel()) {
      win.clearTimeout(timeoutId);
      resolve();
    }
  })
);

const playFlashMarker = ({
  document: doc,
  window: win,
  flashStyle,
  flashDurationMs,
  shouldCancel,
}) => (
  new Promise((resolve) => {
    if (!doc?.body || !win || shouldCancel()) {
      resolve();
      return;
    }

    removeElementById(doc, FLASH_OVERLAY_ID);

    const overlay = doc.createElement("div");
    overlay.id = FLASH_OVERLAY_ID;
    overlay.setAttribute("aria-hidden", "true");
    overlay.style.position = "fixed";
    overlay.style.inset = "0";
    overlay.style.zIndex = "2147483646";
    overlay.style.pointerEvents = "none";
    overlay.style.background = flashStyle === "white-flash" ? "#ffffff" : "#000000";
    overlay.style.opacity = "1";
    overlay.style.transition = `opacity ${DEFAULT_FADE_MS}ms linear`;
    doc.body.appendChild(overlay);

    const holdMs = flashStyle === "black-hold"
      ? clampNumber(flashDurationMs, 50, 5000, ADMIN_VIDEO_SCROLL_DEFAULTS.flashDurationMs)
      : 150;

    win.setTimeout(() => {
      if (shouldCancel()) {
        removeElementById(doc, FLASH_OVERLAY_ID);
        resolve();
        return;
      }

      overlay.style.opacity = "0";
      win.setTimeout(() => {
        removeElementById(doc, FLASH_OVERLAY_ID);
        resolve();
      }, DEFAULT_FADE_MS);
    }, holdMs);
  })
);

const scrollLinearly = ({
  target,
  window: win,
  startTop,
  endTop,
  durationMs,
  shouldCancel,
  onProgress,
}) => (
  new Promise((resolve) => {
    if (!target || !win || durationMs <= 0 || shouldCancel()) {
      if (target) target.scrollTop = endTop;
      resolve();
      return;
    }

    let startTime = null;

    const step = (timestamp) => {
      if (shouldCancel()) {
        resolve();
        return;
      }

      if (startTime == null) {
        startTime = timestamp;
      }

      const progress = Math.min(1, (timestamp - startTime) / durationMs);
      target.scrollTop = startTop + ((endTop - startTop) * progress);

      if (onProgress) {
        onProgress(progress);
      }

      if (progress >= 1) {
        resolve();
        return;
      }

      win.requestAnimationFrame(step);
    };

    win.requestAnimationFrame(step);
  })
);

export function startAdminVideoScroll(options = {}) {
  const doc = getDocument(options.document);
  const win = getWindow(options.window);
  const target = resolveAdminVideoScrollTarget(doc);
  const settings = {
    ...ADMIN_VIDEO_SCROLL_DEFAULTS,
    ...options,
    pixelsPerSecond: clampNumber(
      options.pixelsPerSecond,
      10,
      2000,
      ADMIN_VIDEO_SCROLL_DEFAULTS.pixelsPerSecond,
    ),
    flashDurationMs: clampNumber(
      options.flashDurationMs,
      50,
      5000,
      ADMIN_VIDEO_SCROLL_DEFAULTS.flashDurationMs,
    ),
    pauseAtBottomMs: clampNumber(
      options.pauseAtBottomMs,
      0,
      10000,
      ADMIN_VIDEO_SCROLL_DEFAULTS.pauseAtBottomMs,
    ),
  };

  let cancelled = false;
  let doneResolve;
  let doneReject;
  const done = new Promise((resolve, reject) => {
    doneResolve = resolve;
    doneReject = reject;
  });

  const cleanup = () => {
    removeElementById(doc, FLASH_OVERLAY_ID);
    removeElementById(doc, LEGACY_PROGRESS_PILL_ID);
    doc?.documentElement?.classList?.remove(HIDE_CURSOR_CLASS);
    removeElementById(doc, HIDE_CURSOR_STYLE_ID);
  };

  const controller = {
    cancel() {
      cancelled = true;
      cleanup();
    },
    done,
  };

  const shouldCancel = () => cancelled;

  const run = async () => {
    if (!doc || !win || !target || !isScrollableElement(target)) {
      throw new Error("Page has no scrollable content.");
    }

    if (settings.scrollToTopFirst) {
      target.scrollTop = 0;
    }

    const distance = getAdminVideoScrollDistance(target);
    if (distance <= 1) {
      throw new Error("Page has no scrollable content.");
    }

    const restoreMouseCursor = settings.hideMouseWhileScrolling
      ? applyMouseCursorHiding(doc)
      : () => {};

    await playFlashMarker({
      document: doc,
      window: win,
      flashStyle: settings.flashStyle,
      flashDurationMs: settings.flashDurationMs,
      shouldCancel,
    });

    if (shouldCancel()) return;

    const startTop = target.scrollTop;
    const endTop = target.scrollHeight - target.clientHeight;
    const durationMs = calculateAdminVideoScrollDurationMs(endTop - startTop, settings.pixelsPerSecond);

    await scrollLinearly({
      target,
      window: win,
      startTop,
      endTop,
      durationMs,
      shouldCancel,
      onProgress: settings.onProgress,
    });

    if (!shouldCancel() && settings.pauseAtBottomMs > 0) {
      await waitForTimeout(win, settings.pauseAtBottomMs, shouldCancel);
    }

    restoreMouseCursor();
  };

  run()
    .then(() => {
      cleanup();
      doneResolve({ cancelled });
    })
    .catch((error) => {
      cleanup();
      doneReject(error);
    });

  return controller;
}
